import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { StaffLanguage, type AppState, type UserMedia } from '../types';
import { aniListService } from '../services/aniList';
import { lightTheme, darkTheme } from '../theme';

const getMediaListsFromLocalStorage = (): UserMedia[] => {
    const storedData = localStorage.getItem('userMediaList');
    return storedData ? JSON.parse(storedData) : [];
};

const getInitialTheme = (): boolean => {
    const stored = localStorage.getItem('isDarkTheme');
    if (stored !== null) {
        return stored === 'true';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const useStore = create<AppState>()(
    devtools(
        (set, get) => ({
            userMediaList: getMediaListsFromLocalStorage(),
            isDarkTheme: getInitialTheme(),
            isLoading: false,
            error: null,
            initialized: false,
            userName: localStorage.getItem('userName') || '',
            language: (localStorage.getItem('language') as StaffLanguage) || StaffLanguage.English,
            selectedMediaId: null,

            setIsDarkTheme: (isDark) => {
                localStorage.setItem('isDarkTheme', String(isDark));
                document.documentElement.style.backgroundColor = 
                    isDark ? darkTheme.colorNeutralBackground1 : lightTheme.colorNeutralBackground1;
                set({ isDarkTheme: isDark });
            },

            setError: (error) => set({ error }),
            setLoading: (isLoading) => set({ isLoading }),

            setUserName: (userName) => {
                localStorage.setItem('userName', userName);
                set({ userName });
            },

            setLanguage: (language) => {
                localStorage.setItem('language', language);
                set({ language });
            },

            setSelectedMediaId: (id) => set({ selectedMediaId: id }),

            fetchMediaLists: async () => {
                const state = get();
                // Only fetch if we aren't already loading
                if (state.isLoading) return;

                try {
                    localStorage.removeItem('userMediaList');
                    set({ isLoading: true, error: null, userMediaList: [] });
                    
                    const data = await aniListService.fetchUserMedia(state.userName, state.language);
                    localStorage.setItem('userMediaList', JSON.stringify(data));
                    set({ userMediaList: data });
                } catch (error) {
                    console.error('Failed to fetch API data:', error);
                    set({ error: error as Error });
                } finally {
                    set({ isLoading: false });
                }
            }
        }),
        { name: 'AppStore' }
    )
);

export default useStore;